import * as Phaser from 'phaser';
import { HudUI } from '../ui/HudUI';
import { parseFunction } from '../core/parser';
import { computeTrajectory, DEFAULT_FIELD_BOUNDS, type TrajectoryPoint, createCoordMapper, type CoordMapper } from '../core/trajectory';
import {
  createMatch,
  getCurrentSoldier,
  applyTurn,
  getAliveCount,
  type MatchState,
  type TurnResult,
  DEFAULT_MATCH_CONFIG,
  type Soldier,
} from '../core/match';
import { p2pManager } from '../core/p2p';
import { TeamMode } from '../ui/MenuUI';
import { playShootSound, playExplosionSound, playDeathSound, playVictorySound, playDefeatSound, playTickSound } from '../core/audio';

interface SavedTrajectory {
  teamIndex: number;
  points: TrajectoryPoint[];
}

export class P2PScene extends Phaser.Scene {
  private isHost: boolean = false;
  private mode!: TeamMode;

  private matchState!: MatchState;
  private mapper!: CoordMapper;
  private dynamicBounds = DEFAULT_FIELD_BOUNDS; // fallback

  private obstacleGraphics!: Phaser.GameObjects.Graphics;
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;
  private soldierGroup!: Phaser.GameObjects.Group;
  private explosionGraphics!: Phaser.GameObjects.Graphics;

  private hudUI!: HudUI;
  private gridLabels: Phaser.GameObjects.Text[] = [];
  private soldierLabels: Phaser.GameObjects.Text[] = [];

  private pastTrajectories: SavedTrajectory[] = [];
  private isAnimating: boolean = false;
  private secondsRemaining: number = 0;
  private turnTimerEvent: Phaser.Time.TimerEvent | null = null;
  private lastKillingExpression: string | null = null;

  private readonly TEAM_COLORS = [0x1D4E89, 0xF76707];
  private readonly TRAJECTORY_COLORS = [0x1D4E89, 0xF76707];

  constructor() {
    super({ key: 'P2PScene' });
  }

  init(data: { isHost: boolean; mode: TeamMode }): void {
    this.isHost = data.isHost;
    this.mode = data.mode;
    this.lastKillingExpression = null;
    this.pastTrajectories = [];
    this.isAnimating = false;
    if (this.hudUI) {
      this.hudUI.destroy();
    }
    this.hudUI = new HudUI();
  }

  create(): void {
    const { width, height } = this.scale;
    this.mapper = createCoordMapper(width, height) as any;
    this.dynamicBounds = (this.mapper as any).bounds || DEFAULT_FIELD_BOUNDS;

    this.gridGraphics = this.add.graphics();
    this.obstacleGraphics = this.add.graphics();
    this.trajectoryGraphics = this.add.graphics();
    this.soldierGroup = this.add.group();
    this.explosionGraphics = this.add.graphics();

    this.drawGrid();

    p2pManager.on('SYNC_STATE', (state: MatchState) => this.handleSyncState(state));
    p2pManager.on('TURN_ACTION', (expr: string) => this.handleNetworkTurn(expr));
    p2pManager.on('CHAT_MSG', (msg: string) => this.handleChatMsg(msg));
    p2pManager.on('DISCONNECT', () => this.handleDisconnect());
    p2pManager.on('REQUEST_SYNC', () => {
      if (this.isHost && this.matchState) {
        p2pManager.send('SYNC_STATE', this.matchState);
      }
    });

    this.events.on('shutdown', () => {
      if (this.hudUI) this.hudUI.destroy();
    });

    if (this.isHost) {
      const matchConfig = {
        ...DEFAULT_MATCH_CONFIG,
        team1Count: this.mode.team1Count,
        team2Count: this.mode.team2Count,
        obstacleDensity: this.mode.obstacleDensity,
        bounds: this.dynamicBounds,
      };
      this.matchState = createMatch(matchConfig);
      
      this.matchState.teams[0].name = p2pManager.playerName;
      this.matchState.teams[1].name = p2pManager.opponentName;
      
      this.matchState.teams[0].soldiers.forEach((s: Soldier) => s.isBot = false);
      this.matchState.teams[1].soldiers.forEach((s: Soldier) => s.isBot = true); // Using isBot to signify remote for now if needed

      p2pManager.send('SYNC_STATE', this.matchState);
      this.startGame();
    } else {
      // We no longer need to call hudUI.show() here if startGame() does it later, 
      // but if we want a waiting screen while map syncs:
      this.hudUI.show({ onSubmitFunction: () => {}, onNewGame: () => {}, onBackToMenu: () => {} });
      this.hudUI.showToast('Waiting for host to sync map...');
      p2pManager.send('REQUEST_SYNC', {});
    }
  }

  private handleSyncState(state: MatchState): void {
    this.matchState = state;
    this.startGame();
  }

  private handleDisconnect(): void {
    this.stopTurnTimer();
    this.hudUI.showToast('Opponent disconnected.');
    this.hudUI.setInputEnabled(false);
    
    // Auto-win for the remaining player
    const myTeamIndex = this.isHost ? 0 : 1;
    this.handleMatchEnd(myTeamIndex);
  }

  private handleChatMsg(msg: string): void {
    this.hudUI.addChatEntry(p2pManager.opponentName, msg, false);
    
    const teamIdx = this.isHost ? 1 : 0;
    const oppTeam = this.matchState.teams[teamIdx];
    if (oppTeam) {
       const active = oppTeam.soldiers.find(s => s.alive);
       if (active) this.showChatBubble(active.id, msg);
    }
  }

  private startGame(): void {
    this.drawObstacles();
    this.drawSoldiers();

    this.hudUI.show({
      onSubmitFunction: (expr: string, force?: boolean) => this.handleLocalTurn(expr, force),
      onNewGame: () => {
        p2pManager.disconnect();
        this.scene.start('MenuScene');
      },
      onBackToMenu: () => {
        p2pManager.disconnect();
        this.hudUI.destroy();
        this.scene.start('MenuScene');
      },
      onChatSend: (msg: string) => {
        p2pManager.send('CHAT_MSG', msg);
        const playerName = localStorage.getItem('mathillery_player_name') || 'YOU';
        this.hudUI.addChatEntry(playerName.toUpperCase(), msg, true);
        
        // Show local chat bubble over my active soldier
        const teamIdx = this.isHost ? 0 : 1;
        const myTeam = this.matchState.teams[teamIdx];
        if (myTeam) {
           const active = myTeam.soldiers.find(s => s.alive);
           if (active) this.showChatBubble(active.id, msg);
        }
      }
    });

    this.updateHudState();
  }

  private drawGridLines(g: Phaser.GameObjects.Graphics): void {
    const m = this.mapper;
    const bounds = this.dynamicBounds;

    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    g.lineStyle(1, majorColor, 1);
    g.beginPath();
    const startXMajor = Math.ceil(bounds.minX / 5) * 5;
    for (let x = startXMajor; x <= bounds.maxX; x += 5) {
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMajor = Math.ceil(bounds.minY / 5) * 5;
    for (let y = startYMajor; y <= bounds.maxY; y += 5) {
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    g.lineStyle(1, minorColor, 1);
    g.beginPath();
    const startXMinor = Math.ceil(bounds.minX);
    for (let x = startXMinor; x <= bounds.maxX; x += 1) {
      if (x % 5 === 0) continue;
      const sx = m.toScreenX(x);
      g.moveTo(sx, m.toScreenY(bounds.maxY));
      g.lineTo(sx, m.toScreenY(bounds.minY));
    }
    const startYMinor = Math.ceil(bounds.minY);
    for (let y = startYMinor; y <= bounds.maxY; y += 1) {
      if (y % 5 === 0) continue;
      const sy = m.toScreenY(y);
      g.moveTo(m.toScreenX(bounds.minX), sy);
      g.lineTo(m.toScreenX(bounds.maxX), sy);
    }
    g.strokePath();

    g.lineStyle(2, axisColor, 1);
    g.beginPath();
    const yZero = m.toScreenY(0);
    g.moveTo(m.toScreenX(bounds.minX), yZero);
    g.lineTo(m.toScreenX(bounds.maxX), yZero);

    const xZero = m.toScreenX(0);
    g.moveTo(xZero, m.toScreenY(bounds.minY));
    g.lineTo(xZero, m.toScreenY(bounds.maxY));
    g.strokePath();
  }

  private drawGrid(): void {
    const g = this.gridGraphics;
    g.clear();
    
    this.drawGridLines(g);

    const m = this.mapper;
    const bounds = this.dynamicBounds;
    const yZero = m.toScreenY(0);
    const xZero = m.toScreenX(0);
    const startXMajor = Math.ceil(bounds.minX / 5) * 5;
    const startYMajor = Math.ceil(bounds.minY / 5) * 5;

    for (const lbl of this.gridLabels) {
      lbl.destroy();
    }
    this.gridLabels = [];

    const textColor = '#8A8780'; 

    const originLabel = this.add.text(xZero - 8, yZero + 6, '0', {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '10px',
      color: textColor,
    }).setOrigin(1, 0);
    this.gridLabels.push(originLabel);

    for (let x = startXMajor; x <= bounds.maxX; x += 5) {
      if (x === 0) continue;
      const lbl = this.add.text(m.toScreenX(x), yZero + 6, String(x), {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '10px',
        color: textColor,
      }).setOrigin(0.5, 0);
      this.gridLabels.push(lbl);
    }

    for (let y = startYMajor; y <= bounds.maxY; y += 5) {
      if (y === 0) continue;
      const lbl = this.add.text(xZero - 8, m.toScreenY(y), String(y), {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '10px',
        color: textColor,
      }).setOrigin(1, 0.5);
      this.gridLabels.push(lbl);
    }
  }

  private drawObstacles(): void {
    if (!this.obstacleGraphics || !this.matchState) return;
    this.obstacleGraphics.clear();
    const m = this.mapper;

    const majorColor = 0xd8d5ce;
    const minorColor = 0xeae8e3;
    const axisColor = 0xb4b2a9;

    for (const obs of this.matchState.obstacles) {
      const sx = m.toScreenX(obs.x);
      const sy = m.toScreenY(obs.y);
      const sRadius = Math.abs(m.toScreenX(obs.x + obs.radius) - sx);

      const obsColor = 0x2B2A28; 
      const obsStroke = 0x2B2A28;

      this.obstacleGraphics.fillStyle(obsColor, 1.0);
      this.obstacleGraphics.fillCircle(sx, sy, sRadius);
      this.obstacleGraphics.lineStyle(2, obsStroke, 1.0);
      this.obstacleGraphics.strokeCircle(sx, sy, sRadius);

      const bgColor = 0xF8F7F4;

      for (const bite of obs.bites) {
        const bx = m.toScreenX(bite.x);
        const by = m.toScreenY(bite.y);
        const br = Math.abs(m.toScreenX(obs.x + bite.radius) - m.toScreenX(obs.x));
        
        this.obstacleGraphics.fillStyle(bgColor, 1.0);
        this.obstacleGraphics.fillCircle(bx, by, br);

        // Mathematical Grid Clipping
        // Draw the exact grid line segments that fall within this bite circle
        const drawVertLine = (x: number, color: number, width: number) => {
            if (Math.abs(x - bite.x) < bite.radius) {
                const dy = Math.sqrt(bite.radius * bite.radius - (x - bite.x) ** 2);
                const sy1 = m.toScreenY(bite.y - dy);
                const sy2 = m.toScreenY(bite.y + dy);
                const px = m.toScreenX(x);
                this.obstacleGraphics.lineStyle(width, color, 1);
                this.obstacleGraphics.beginPath();
                this.obstacleGraphics.moveTo(px, sy1);
                this.obstacleGraphics.lineTo(px, sy2);
                this.obstacleGraphics.strokePath();
            }
        };

        const drawHorizLine = (y: number, color: number, width: number) => {
            if (Math.abs(y - bite.y) < bite.radius) {
                const dx = Math.sqrt(bite.radius * bite.radius - (y - bite.y) ** 2);
                const sx1 = m.toScreenX(bite.x - dx);
                const sx2 = m.toScreenX(bite.x + dx);
                const py = m.toScreenY(y);
                this.obstacleGraphics.lineStyle(width, color, 1);
                this.obstacleGraphics.beginPath();
                this.obstacleGraphics.moveTo(sx1, py);
                this.obstacleGraphics.lineTo(sx2, py);
                this.obstacleGraphics.strokePath();
            }
        };

        const startX = Math.ceil(bite.x - bite.radius);
        const endX = Math.floor(bite.x + bite.radius);
        for (let x = startX; x <= endX; x++) {
            if (x === 0) {
                drawVertLine(x, axisColor, 2);
            } else if (x % 5 === 0) {
                drawVertLine(x, majorColor, 1);
            } else {
                drawVertLine(x, minorColor, 1);
            }
        }

        const startY = Math.ceil(bite.y - bite.radius);
        const endY = Math.floor(bite.y + bite.radius);
        for (let y = startY; y <= endY; y++) {
            if (y === 0) {
                drawHorizLine(y, axisColor, 2);
            } else if (y % 5 === 0) {
                drawHorizLine(y, majorColor, 1);
            } else {
                drawHorizLine(y, minorColor, 1);
            }
        }
      }
    }
  }

  private drawSoldiers(): void {
    this.soldierGroup.clear(true, true);
    const m = this.mapper;

    if (!this.matchState) return;

    const currentSoldier = getCurrentSoldier(this.matchState);

    for (const [colorIndex, team] of this.matchState.teams.entries()) {
      const teamColorStr = colorIndex === 0 ? '#1D4E89' : '#d94040';
      const teamColorHex = colorIndex === 0 ? 0x1D4E89 : 0xd94040;

      for (const soldier of team.soldiers) {
        const screenX = m.toScreenX(soldier.x);
        const screenY = m.toScreenY(soldier.y);

        if (!soldier.alive) {
          const xText = this.add.text(screenX, screenY, 'X', {
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: '20px',
            color: teamColorStr,
            fontStyle: 'bold',
            stroke: '#ffffff',
            strokeThickness: 3,
          }).setOrigin(0.5);
          this.soldierGroup.add(xText);
          continue;
        }

        const isCurrent = soldier.id === currentSoldier.id;

        const container = this.add.container(screenX, screenY);
        container.setName(`soldier-container-${soldier.id}`);
        this.soldierGroup.add(container);

        const hitRadius = this.matchState.config.hitRadius || 0.5;
        const SOLDIER_RADIUS = m.toScreenX(hitRadius) - m.toScreenX(0);

        const circle = this.add.circle(0, 0, SOLDIER_RADIUS, teamColorHex);
        circle.setStrokeStyle(2, 0xffffff, 1.0);
        container.add(circle);

        if (this.mode.showCoordinates) {
          const posText = `(${soldier.x.toFixed(1)}, ${soldier.y.toFixed(1)})`;
          const posLabel = this.add.text(0, SOLDIER_RADIUS + 4, posText, {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '10px',
            color: '#6e6e7e',
          }).setOrigin(0.5, 0);
          container.add(posLabel);
        }

        // Name label container
        const textGroup = this.add.container(0, -SOLDIER_RADIUS - 18);
        
        const isMyTeam = (colorIndex === 0 && this.isHost) || (colorIndex === 1 && !this.isHost);
        const labelText = isMyTeam ? 'YOU' : team.name.toUpperCase();
        const label = this.add.text(0, 0, labelText, {
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: '11px',
          fontStyle: 'bold',
          color: teamColorStr,
          stroke: '#ffffff',
          strokeThickness: 3,
        }).setOrigin(0.5, 1);
        textGroup.add(label);

        if (isCurrent) {
          const arrow = this.add.text(0, 10, '▼', {
            fontFamily: 'Outfit',
            fontSize: '10px',
            color: teamColorStr,
            stroke: '#ffffff',
            strokeThickness: 2,
          }).setOrigin(0.5);
          
          this.tweens.add({
            targets: arrow,
            y: 13,
            duration: 600,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
          });
          textGroup.add(arrow);
        }

        container.add(textGroup);
      }
    }
  }

  private showChatBubble(soldierId: string, message: string): void {
    const soldier = this.matchState.teams.flatMap(t => t.soldiers).find(s => s.id === soldierId);
    if (!soldier) return;

    // Check if there's an existing bubble and destroy it
    const existingBubble = this.children.getByName(`chat-bubble-${soldierId}`);
    if (existingBubble) {
      existingBubble.destroy();
    }

    const sx = this.mapper.toScreenX(soldier.x);
    const sy = this.mapper.toScreenY(soldier.y);

    const bubbleGroup = this.add.container(sx, sy - 45);
    bubbleGroup.setName(`chat-bubble-${soldierId}`);
    
    const text = this.add.text(0, -8, message, {
      fontFamily: '"Space Grotesk", sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#1e293b',
      align: 'center',
    }).setOrigin(0.5);

    const paddingX = 14;
    const paddingY = 8;
    const width = text.width + paddingX * 2;
    const height = text.height + paddingY * 2;
    const radius = 12;

    const graphics = this.add.graphics();
    
    const w = width;
    const h = height;
    const cx = -w/2;
    const cy = -h/2 - 8;

    const drawBubblePath = (g: Phaser.GameObjects.Graphics) => {
      g.beginPath();
      g.moveTo(cx + radius, cy);
      g.lineTo(cx + w - radius, cy);
      g.arc(cx + w - radius, cy + radius, radius, -Math.PI/2, 0);
      g.lineTo(cx + w, cy + h - radius);
      g.arc(cx + w - radius, cy + h - radius, radius, 0, Math.PI/2);
      
      // Tail
      g.lineTo(8, cy + h);
      g.lineTo(0, cy + h + 8);
      g.lineTo(-8, cy + h);
      
      g.lineTo(cx + radius, cy + h);
      g.arc(cx + radius, cy + h - radius, radius, Math.PI/2, Math.PI);
      g.lineTo(cx, cy + radius);
      g.arc(cx + radius, cy + radius, radius, Math.PI, -Math.PI/2);
      g.closePath();
    };

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.translateCanvas(0, 4);
    drawBubblePath(graphics);
    graphics.fillPath();
    graphics.translateCanvas(0, -4);

    // Bubble Body
    graphics.fillStyle(0xffffff, 1);
    drawBubblePath(graphics);
    graphics.fillPath();
    
    // Bubble Stroke (Primary Blue)
    graphics.lineStyle(2, 0x1D4E89, 1);
    graphics.strokePath();

    bubbleGroup.add(graphics);
    bubbleGroup.add(text);
    
    bubbleGroup.setScale(1);
    bubbleGroup.setAlpha(1);

    this.time.delayedCall(3500, () => {
      if (bubbleGroup) bubbleGroup.destroy();
    });
  }

  private handleLocalTurn(expressionStr: string, force?: boolean): void {
    if (this.isAnimating) return;
    
    const currentSoldier = getCurrentSoldier(this.matchState);
    const currentTeamIndex = currentSoldier.teamIndex;
    const isMyTurn = (currentTeamIndex === 0 && this.isHost) || (currentTeamIndex === 1 && !this.isHost);
    
    if (!isMyTurn) {
      this.hudUI.showToast("It's not your turn!");
      return;
    }

    this.stopTurnTimer();

    let result = parseFunction(expressionStr);
    if (!result.ok && force) { 
      result = parseFunction('0');
    } else if (!result.ok) {
      this.hudUI.showError(result.message);
      return;
    }

    if (!result.ok) return;

    // Send to opponent
    p2pManager.send('TURN_ACTION', result.expression);

    // Execute locally
    this.executeTurnLocally(result.expression, result.evaluate);
  }

  private handleNetworkTurn(expressionStr: string): void {
    if (this.isAnimating) return;
    const result = parseFunction(expressionStr);
    if (result.ok) {
      this.executeTurnLocally(result.expression, result.evaluate);
    }
  }

  private executeTurnLocally(expressionStr: string, evaluateFn: (x: number) => number): void {
    this.isAnimating = true;
    this.hudUI.setInputEnabled(false);
    this.hudUI.hideError();

    const currentSoldier = getCurrentSoldier(this.matchState);
    const currentTeamIndex = currentSoldier.teamIndex;
    
    const turnResult = applyTurn(this.matchState, evaluateFn);

    if (turnResult.kills.length > 0) {
      this.lastKillingExpression = expressionStr;
    }

    this.hudUI.addHistoryEntry({
      teamIndex: currentTeamIndex,
      soldierLabel: `S${currentSoldier.id}`,
      expression: expressionStr,
      hits: turnResult.kills.length,
    });
    playShootSound();

    if (this.mode.chatEquations) {
      this.showChatBubble(currentSoldier.id, `f(x) = ${expressionStr}`);
    }

    this.animateTrajectory(
      turnResult.trajectory,
      currentTeamIndex,
      () => {
        if (this.mode.keepTraces) {
          this.pastTrajectories.push({
            points: turnResult.trajectory,
            teamIndex: currentTeamIndex, 
          });
        }

        this.drawObstacles();
        this.drawSoldiers();
        this.drawPastTrajectories();

        this.explosionGraphics.clear();
        this.isAnimating = false;

        if (turnResult.matchOver) {
          this.handleMatchEnd(turnResult.winnerIndex);
        } else {
          this.updateHudState();
        }
      },
      turnResult.kills
    );
  }

  private animateTrajectory(
    points: { x: number; y: number }[],
    teamIndex: number,
    onComplete: () => void,
    kills: any[]
  ): void {
    const g = this.trajectoryGraphics;
    const m = this.mapper;

    g.clear();
    const color = this.TRAJECTORY_COLORS[teamIndex];

    const POINTS_PER_FRAME = 2;
    let i = 0;

    const timer = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        g.clear();
        this.drawPastTrajectories();
        
        const limit = Math.min(i + POINTS_PER_FRAME, points.length);
        
        g.lineStyle(3, color, 1.0);
        g.beginPath();
        if (points.length > 0) {
          g.moveTo(m.toScreenX(points[0].x), m.toScreenY(points[0].y));
          for (let j = 1; j < limit; j++) {
            g.lineTo(m.toScreenX(points[j].x), m.toScreenY(points[j].y));
          }
          g.strokePath();
        }

        if (limit > 0) {
          const lastP = points[limit - 1];
          g.fillStyle(color, 1.0);
          g.fillCircle(m.toScreenX(lastP.x), m.toScreenY(lastP.y), 4);
        }

        if (limit >= points.length) {
          timer.remove();
          playExplosionSound();
          if (points.length > 0) {
            const lastP = points[points.length - 1];
            this.showExplosion(lastP.x, lastP.y, color, onComplete, kills);
          } else {
            onComplete();
          }
        }
        i = limit;
      }
    });
  }

  private showExplosion(x: number, y: number, color: number, onComplete: () => void, kills: any[]): void {
    const eg = this.explosionGraphics;
    const m = this.mapper;
    const sx = m.toScreenX(x);
    const sy = m.toScreenY(y);

    let radius = 0;
    // P2P scene default explosion radius: let's use 2 as fallback
    const targetRadius = m.toScreenX(2) - m.toScreenX(0);

    const hitSxs = kills.map(s => m.toScreenX(s.x));
    const hitSys = kills.map(s => m.toScreenY(s.y));
    const hitRadii = kills.map(() => m.toScreenX(0.5) - m.toScreenX(0));

    const animEvent = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        radius += targetRadius / 10;
        eg.clear();
        
        eg.fillStyle(color, 1.0 - (radius / targetRadius));
        eg.fillCircle(sx, sy, radius);
        eg.lineStyle(2, 0xffffff, 1.0 - (radius / targetRadius));
        eg.strokeCircle(sx, sy, radius);

        for (let k = 0; k < hitSxs.length; k++) {
          eg.fillStyle(0xffffff, (radius / targetRadius));
          eg.fillCircle(hitSxs[k], hitSys[k], hitRadii[k] + (radius * 0.2));
        }

        if (radius >= targetRadius) {
          animEvent.remove();
          if (kills.length > 0) playDeathSound();
          onComplete();
        }
      }
    });
  }

  private drawPastTrajectories(): void {
    const g = this.trajectoryGraphics;
    const m = this.mapper;

    for (const traj of this.pastTrajectories) {
      g.lineStyle(1.5, this.TRAJECTORY_COLORS[traj.teamIndex], 0.15);
      g.beginPath();
      if (traj.points.length > 0) {
        g.moveTo(m.toScreenX(traj.points[0].x), m.toScreenY(traj.points[0].y));
        for (let j = 1; j < traj.points.length; j++) {
          g.lineTo(m.toScreenX(traj.points[j].x), m.toScreenY(traj.points[j].y));
        }
      }
      g.strokePath();
    }
  }

  private updateHudState(): void {
    const alive = getAliveCount(this.matchState);
    this.hudUI.updateScore(alive[0], alive[1]);

    const currentSoldier = getCurrentSoldier(this.matchState);
    const currentTeamIndex = currentSoldier.teamIndex;
    const isMyTurn = (currentTeamIndex === 0 && this.isHost) || (currentTeamIndex === 1 && !this.isHost);

    if (!isMyTurn) {
      const opponentName = this.matchState.teams[currentTeamIndex].name;
      this.hudUI.setTurnIndicator(`${opponentName}'s turn`, false);
      this.hudUI.setInputEnabled(false);
      this.stopTurnTimer();
    } else {
      this.hudUI.setTurnIndicator('Your turn', true);
      this.hudUI.setInputEnabled(true);
      this.startTurnTimer();
    }
  }

  private startTurnTimer(): void {
    this.stopTurnTimer();
    if (this.mode.turnTimer <= 0) return;

    this.secondsRemaining = this.mode.turnTimer;
    this.hudUI.updateTimer(this.secondsRemaining);

    this.turnTimerEvent = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.secondsRemaining--;
        this.hudUI.updateTimer(this.secondsRemaining);
        
        if (this.secondsRemaining <= 5 && this.secondsRemaining > 0) {
          playTickSound();
        }

        if (this.secondsRemaining <= 0) {
          this.stopTurnTimer();
          this.hudUI.forceFire();
        }
      }
    });
  }

  private stopTurnTimer(): void {
    if (this.turnTimerEvent) {
      this.turnTimerEvent.remove();
      this.turnTimerEvent = null;
    }
    this.hudUI.hideTimer();
  }

  private handleMatchEnd(winnerIndex: number): void {
    this.hudUI.setInputEnabled(false);
    this.stopTurnTimer();
    
    const myTeamIndex = this.isHost ? 0 : 1;
    let didIWin = (winnerIndex === myTeamIndex);
    
    if (didIWin) playVictorySound();
    else playDefeatSound();

    const winnerName = (winnerIndex === 0) ? this.matchState.teams[0].name : this.matchState.teams[1].name;

    setTimeout(() => {
      this.hudUI.showResult(didIWin, winnerName, this.lastKillingExpression || 'f(x) = x');
    }, 1000);
  }
}
